import posthog from "posthog-js";

/**
 * PostHog has to be usable from the *first* mount effect of the *first* route.
 *
 * `posthog.capture()` is a silent no-op until `posthog.init()` has run — it only logs
 * "You must initialize PostHog before calling posthog.capture" and drops the event. Init used to
 * live behind a `mounted` state round-trip in `PHProvider`, which pushed it to the *second*
 * commit after mount, while React runs child effects *before* parent effects. So every event a
 * route fired on mount was thrown away, and nothing in the UI showed it. The visible symptom was
 * `discount_creation_initiated` never arriving from the `/app/templates?type=…` deep links,
 * which track and then redirect inside a single commit.
 *
 * The fix is the buffer, not earlier init: `capturePostHog` holds anything captured before the
 * client is live and `initPostHogOnce` replays it. That is what makes the ordering between a
 * route's effects and the provider's effect stop mattering — so init can stay in an effect,
 * where browser APIs belong, instead of running during render.
 */

type PendingCapture = { event: string; properties: Record<string, any> };

/** Bounded so a missing API key cannot grow the buffer without limit. */
const PENDING_LIMIT = 100;

const pending: PendingCapture[] = [];
let initialized = false;
/** Set if `posthog.init` threw: there is no recovery, so stop buffering and stop retrying. */
let initFailed = false;

export function isPostHogReady(): boolean {
  return initialized;
}

/**
 * Idempotent. Returns whether PostHog is live — false on the server, in dev, when no API key is
 * configured, and if the SDK itself failed to start.
 *
 * Call it from an effect, never from render: it touches cookies and localStorage, which an
 * embedded Shopify iframe can have partitioned or blocked outright, and a throw during render
 * would take the whole authenticated app's first paint with it.
 */
export function initPostHogOnce(args: {
  apiKey?: string;
  host?: string;
  isDevEnv: boolean;
  shopDomain?: string;
  pathname: string;
  search: string;
}): boolean {
  if (initialized) return true;
  if (initFailed) return false;
  if (typeof window === "undefined") return false;
  if (!args.apiKey || args.isDevEnv) return false;

  const registered = {
    $current_url: `https://${args.shopDomain}${args.pathname}${args.search}`,
    $host: args.shopDomain,
    $pathname: args.pathname,
  };

  try {
    posthog.init(args.apiKey, {
    api_host: args.host || "https://us.i.posthog.com",
    // Must match a version your posthog-js bundle understands (see ConfigDefaults in SDK types).
    defaults: "2025-05-24",
    person_profiles: "identified_only",
    capture_pageview: false,
    capture_pageleave: true,
    disable_session_recording: true,
    loaded: (ph) => {
      ph.register(registered);
    },
    autocapture: {
      url_allowlist: [
        `https://${args.shopDomain}/*`,
        "https://admin.shopify.com/*",
      ],
    },
    capture_performance: false,
  });

    // `loaded` stays as the SDK's own hook, but the same registration runs here so the URL
    // properties are set even if `loaded` fires a tick late — the replay below must not go out
    // with the wrong `$current_url`.
    posthog.register(registered);

    // Identify before replaying so events captured during the first commit are attributed to the
    // shop rather than to an anonymous device id. `PHProvider` re-identifies with the full
    // plan/user properties once the shop store has loaded; this is the same person id.
    if (args.shopDomain) {
      posthog.identify(args.shopDomain, { shop_domain: args.shopDomain });
    }
    initialized = true;
  } catch (error) {
    // Analytics must never be able to break the app. Blocked storage in a partitioned iframe is
    // the realistic cause; there is no recovery, so stop retrying and release the buffer rather
    // than growing it for a client that will never come up.
    initFailed = true;
    pending.length = 0;
    console.warn(
      "analytics.posthog.init.failed",
      { reason: error instanceof Error ? error.message : String(error) },
      "PostHog failed to initialize",
    );
    return false;
  }

  for (const item of pending.splice(0, pending.length)) {
    posthog.capture(item.event, item.properties);
  }

  return true;
}

export function capturePostHog(
  event: string,
  properties: Record<string, any>,
): void {
  if (initialized) {
    posthog.capture(event, properties);
    return;
  }
  if (initFailed) return;
  if (typeof window === "undefined") return;
  if (pending.length >= PENDING_LIMIT) pending.shift();
  pending.push({ event, properties });
}

/**
 * Push the batched request queue out now.
 *
 * `capture` enqueues with a 3s flush interval (`request_batching`), so a top-level document
 * navigation started right after a capture can tear the frame down with the event still in the
 * queue. The Sidekick post-save breakout does exactly that: `window.open(…, "_top")` one render
 * tick after `discount_created`. This is the same path the SDK runs on `pagehide`, so it uses
 * `sendBeacon` and does not delay the caller.
 */
export function flushPostHog(): void {
  if (!initialized) return;
  try {
    posthog._handle_unload();
  } catch {
    /* best-effort: a flush must never break the navigation it precedes */
  }
}
